import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search, ChevronRight, ShoppingBag, Tag, UserCog, Wallet, ShieldCheck, ArrowLeft, Mail, LifeBuoy, Flame } from "lucide-react";
import { Seo } from "../components/Seo";
import { HELP_CATEGORIES, HELP_ARTICLES, findArticle } from "./info/helpData";
import { Steps, UL } from "./info/ui";

const CAT_ICONS = { kaufen: ShoppingBag, verkaufen: Tag, konto: UserCog, zahlungen: Wallet, sicherheit: ShieldCheck };

function ArticleBlocks({ blocks }) {
  return (
    <div className="space-y-5">
      {blocks.map((b, i) => {
        if (b.p) return <p key={i} className="text-sm text-foreground/85 leading-relaxed">{b.p}</p>;
        if (b.ul) return <UL key={i} items={b.ul} />;
        if (b.steps) return <Steps key={i} items={b.steps} />;
        return null;
      })}
    </div>
  );
}

const articleCard = "card-lift block border border-[#E5E3DA] rounded-2xl p-5 bg-white hover:border-accent/40";

export function HelpArticle() {
  const { slug } = useParams();
  const article = findArticle(slug);
  if (!article) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="help-article-not-found">
        <h1 className="text-3xl font-bold">Artikel nicht gefunden</h1>
        <Link to="/hilfe" className="inline-block mt-6 text-sm font-bold text-accent hover:underline">Zurück zum Hilfe-Center</Link>
      </div>
    );
  }
  const cat = HELP_CATEGORIES.find((c) => c.key === article.cat);
  const related = HELP_ARTICLES.filter((a) => a.cat === article.cat && a.slug !== article.slug).slice(0, 4);
  return (
    <div className="bg-[#FAF9F5]" data-testid="help-article-page">
      <Seo title={`${article.title} – Hilfe`} description={article.teaser} />
      <div className="relative bg-[#111111] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-28 -right-28 w-[380px] h-[380px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          <nav className="fade-up text-xs text-white/60 flex items-center gap-1.5 flex-wrap">
            <Link to="/hilfe" className="hover:text-accent font-semibold">Hilfe-Center</Link>
            <ChevronRight className="w-3 h-3" aria-hidden="true" />
            <span>{cat?.label}</span>
          </nav>
          <h1 className="fade-up-1 font-display text-3xl sm:text-4xl font-bold mt-3 leading-[1.08]">{article.title}</h1>
          <p className="fade-up-2 text-sm md:text-base text-white/70 mt-3 max-w-xl">{article.teaser}</p>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-12">
        <div className="bg-white border border-[#E5E3DA] rounded-3xl p-6 md:p-8"><ArticleBlocks blocks={article.blocks} /></div>
        {related.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-base font-bold mb-4 flex items-center gap-3"><span aria-hidden="true" className="w-1.5 h-5 rounded-full bg-accent inline-block" /> Weitere Artikel zu „{cat?.label}“</h2>
            <div className="grid sm:grid-cols-2 gap-3.5">
              {related.map((a) => (
                <Link key={a.slug} to={`/hilfe/artikel/${a.slug}`} className={articleCard}>
                  <p className="text-sm font-bold">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{a.teaser}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
        <ContactHint />
      </div>
    </div>
  );
}

function ContactHint() {
  return (
    <div className="mt-10 card-lift border border-[#E5E3DA] rounded-2xl p-5 bg-white flex items-center gap-4" data-testid="help-contact">
      <span className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center shrink-0"><Mail className="w-5 h-5" aria-hidden="true" /></span>
      <p className="text-sm">Nichts Passendes gefunden? Schreib uns über das <Link to="/kontakt" className="font-semibold text-accent hover:underline">Kontaktformular</Link> – oder melde Verstöße über die <Link to="/meldestelle" className="font-semibold text-accent hover:underline">Meldestelle</Link>.</p>
    </div>
  );
}

export default function Help() {
  const [q, setQ] = useState("");
  const [activeCat, setActiveCat] = useState(null);
  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return null;
    return HELP_ARTICLES.filter((a) => {
      const text = [a.title, a.teaser, ...a.blocks.flatMap((b) => [b.p || "", ...(b.ul || []), ...((b.steps || []).map((s) => `${s.t} ${s.d}`))])].join(" ").toLowerCase();
      return text.includes(term);
    });
  }, [q]);

  return (
    <div className="bg-[#FAF9F5]" data-testid="help-center-page">
      <Seo title="Hilfe-Center" description="Antworten auf alle Fragen rund um Kaufen, Verkaufen, Konto, Zahlungen und Sicherheit auf KLEDIO." />
      {/* Hero mit großer Suche */}
      <div className="relative bg-[#111111] text-white overflow-hidden">
        <div aria-hidden="true" className="absolute -top-32 -right-32 w-[460px] h-[460px] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
        <div aria-hidden="true" className="absolute -bottom-40 -left-24 w-[360px] h-[360px] rounded-full opacity-[0.12]" style={{ background: "radial-gradient(circle, #FF6B35 0%, transparent 65%)" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-14 md:py-20">
          <p className="fade-up inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.25em] uppercase text-accent">
            <LifeBuoy className="w-4 h-4" /> Hilfe-Center
          </p>
          <h1 className="fade-up-1 font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4 leading-[1.05]">Wie können wir dir helfen?</h1>
          <p className="fade-up-2 text-base md:text-lg text-white/70 mt-4 max-w-xl">Antworten auf alles rund um Kaufen, Verkaufen, Konto, Zahlungen und Sicherheit.</p>
          <div className="fade-up-3 relative mt-8 max-w-xl">
            <Search className="w-5 h-5 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="help-search-input"
              placeholder="Suche nach Themen, z. B. Auszahlung, Versand, Käuferschutz …"
              className="w-full rounded-full pl-12 pr-5 py-4 text-sm bg-white text-foreground shadow-[0_12px_40px_-12px_rgba(0,0,0,0.4)] focus:outline-none focus:ring-2 focus:ring-accent/70" />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        {results ? (
          <div data-testid="help-search-results">
            <p className="text-sm text-muted-foreground mb-4">{results.length} {results.length === 1 ? "Ergebnis" : "Ergebnisse"} für „{q}“</p>
            <div className="space-y-3">
              {results.map((a) => (
                <Link key={a.slug} to={`/hilfe/artikel/${a.slug}`} className={articleCard}>
                  <p className="text-sm font-bold">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.teaser}</p>
                </Link>
              ))}
              {results.length === 0 && <p className="text-sm text-muted-foreground">Keine Artikel gefunden. Versuche einen anderen Begriff oder nutze das <Link to="/kontakt" className="text-accent hover:underline">Kontaktformular</Link>.</p>}
            </div>
          </div>
        ) : activeCat ? (
          <div data-testid="help-category-view">
            <button onClick={() => setActiveCat(null)} className="text-sm font-semibold text-accent flex items-center gap-1.5 mb-5" data-testid="help-back-btn">
              <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Alle Kategorien
            </button>
            <h2 className="font-display text-xl font-bold">{HELP_CATEGORIES.find((c) => c.key === activeCat)?.label}</h2>
            <div className="mt-5 grid sm:grid-cols-2 gap-3.5">
              {HELP_ARTICLES.filter((a) => a.cat === activeCat).map((a) => (
                <Link key={a.slug} to={`/hilfe/artikel/${a.slug}`} className={articleCard} data-testid={`help-article-link-${a.slug}`}>
                  <p className="text-sm font-bold">{a.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{a.teaser}</p>
                </Link>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="help-categories">
              {HELP_CATEGORIES.map((c) => {
                const Icon = CAT_ICONS[c.key];
                const count = HELP_ARTICLES.filter((a) => a.cat === c.key).length;
                return (
                  <button key={c.key} onClick={() => setActiveCat(c.key)} data-testid={`help-cat-${c.key}`}
                    className="card-lift text-left border border-[#E5E3DA] rounded-2xl md:rounded-3xl p-6 bg-white hover:border-accent/40 group">
                    <span className="w-11 h-11 rounded-2xl bg-accent/10 text-accent flex items-center justify-center">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <p className="text-[15px] font-bold mt-4 group-hover:text-accent transition-colors">{c.label}</p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{c.desc}</p>
                    <p className="text-[11px] font-bold text-accent mt-3">{count} Artikel →</p>
                  </button>
                );
              })}
            </div>
            <div className="mt-12">
              <h2 className="font-display text-base md:text-lg font-bold mb-5 flex items-center gap-3">
                <span className="w-9 h-9 rounded-xl bg-accent/10 text-accent flex items-center justify-center"><Flame className="w-4.5 h-4.5 w-5 h-5" aria-hidden="true" /></span> Beliebte Artikel
              </h2>
              <div className="space-y-3">
                {["kaeuferschutz-erklaert", "artikel-einstellen", "auszahlung-verkaeufer", "problem-mit-bestellung", "betrug-erkennen"].map((slug) => {
                  const a = findArticle(slug);
                  return (
                    <Link key={slug} to={`/hilfe/artikel/${slug}`} className="card-lift flex items-center justify-between gap-3 border border-[#E5E3DA] rounded-2xl p-5 bg-white hover:border-accent/40 group">
                      <span className="text-sm font-bold">{a.title}</span>
                      <span className="w-8 h-8 rounded-full bg-accent/10 text-accent flex items-center justify-center shrink-0 transition-transform group-hover:translate-x-0.5">
                        <ChevronRight className="w-4 h-4" aria-hidden="true" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </>
        )}
        <ContactHint />
      </div>
    </div>
  );
}
